import React from 'react';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import { Box, InputBase } from '@mui/material';

const PROPERTY_EDITOR_OPTION_LIST_MAX_HEIGHT = 280;

export interface PropertyEditorSearchFrameProps {
  search: string;
  placeholder: string;
  autoFocus?: boolean;
  disabled?: boolean;
  selectedContent?: React.ReactNode;
  children: React.ReactNode;
  onSearchChange: (value: string) => void;
  onInputFocus?: () => void;
  onEnter?: () => void;
  activeIndex?: number;
  itemCount?: number;
  onActiveIndexChange?: (index: number) => void;
}

export const EditorOptionList = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      maxHeight: `min(${PROPERTY_EDITOR_OPTION_LIST_MAX_HEIGHT}px, calc(100vh - 180px))`,
      overflowY: 'auto',
      overscrollBehavior: 'contain',
      py: 0.5,
      scrollbarWidth: 'thin',
    }}
  >
    {children}
  </Box>
);

export const PropertyEditorSearchFrame = ({
  search,
  placeholder,
  autoFocus = true,
  disabled = false,
  selectedContent,
  children,
  onSearchChange,
  onInputFocus,
  onEnter,
  activeIndex = 0,
  itemCount = 0,
  onActiveIndexChange,
}: PropertyEditorSearchFrameProps) => (
  <Box>
    {selectedContent ? (
      <Box sx={{ borderBottom: `1px solid ${ELIG_TOKENS.line}`, py: 0.5 }}>
        {selectedContent}
      </Box>
    ) : null}
    <Box sx={{ p: 1, borderBottom: `1px solid ${ELIG_TOKENS.line}` }}>
      <InputBase
        autoFocus={autoFocus}
        fullWidth
        placeholder={placeholder}
        value={search}
        disabled={disabled}
        onFocus={onInputFocus}
        onClick={event => event.stopPropagation()}
        onChange={event => onSearchChange(event.target.value)}
        onKeyDown={event => {
          if (disabled) return;

          if (
            (event.key === 'ArrowDown' || event.key === 'ArrowUp') &&
            onActiveIndexChange &&
            itemCount > 0
          ) {
            event.preventDefault();
            event.stopPropagation();
            const delta = event.key === 'ArrowDown' ? 1 : -1;
            onActiveIndexChange((activeIndex + delta + itemCount) % itemCount);
            return;
          }

          if (event.key !== 'Enter' || !onEnter) return;

          event.preventDefault();
          event.stopPropagation();
          onEnter();
        }}
        sx={{
          px: 1,
          py: 0.5,
          borderRadius: 1,
          border: `1px solid ${ELIG_TOKENS.line}`,
          fontSize: 13,
          color: ELIG_TOKENS.ink,
        }}
      />
    </Box>
    <EditorOptionList>{children}</EditorOptionList>
  </Box>
);
